import { showToast } from 'state/toast';
import { t } from '@lingui/core/macro';
import { writeToClipboard } from '../clipboard';

export const copyQrCodeAsImage = async (qrRef: React.RefObject<HTMLDivElement>) => {
  if (!qrRef.current) {
    showToast({ title: t`QR code not found`, type: 'error', notificationId: 'QR_CODE_NOT_FOUND' });
    return;
  }

  try {
    const svg = qrRef.current.querySelector('svg');
    if (!svg) throw new Error('QR Code SVG element not found');

    // Serialize SVG and safely base64-encode to handle UTF-8
    const xml = new XMLSerializer().serializeToString(svg);
    const utf8Bytes = new TextEncoder().encode(xml);
    const svg64 = btoa(
      Array.from(utf8Bytes)
        .map((b) => String.fromCharCode(b))
        .join('')
    );
    const dataUrl = `data:image/svg+xml;base64,${svg64}`;

    // Build ClipboardItem immediately (still inside the gesture) or request will fail due to security
    const item = new ClipboardItem({
      'image/png': new Promise<Blob>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('2D context not available'));

            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              if (!blob) return reject(new Error('Canvas toBlob returned null'));
              resolve(blob);
            }, 'image/png');
          } catch (e) {
            reject(e instanceof Error ? e : new Error(String(e)));
          }
        };
        img.onerror = () => reject(new Error('Failed to load image from data URL'));
        img.src = dataUrl;
      }),
    });

    // Trigger the permission-gated call now (under user activation)
    const result = await writeToClipboard([item], {
      errorMessage: t`Failed to copy QR code`,
    });

    if (result.success) {
      showToast({ title: t`QR code copied to clipboard`, type: 'success', notificationId: 'QR_CODE_COPY_SUCCESS' });
    } else {
      showToast({
        title: t`Failed to copy QR code`,
        message: result.error?.message || t`Unknown error occurred`,
        type: 'error',
        notificationId: 'QR_CODE_COPY_ERROR',
      });
    }
  } catch (error) {
    // Handle errors in image processing/ClipboardItem creation
    showToast({
      title: t`Failed to copy QR code`,
      message: error instanceof Error ? error.message : String(error),
      type: 'error',
      notificationId: 'QR_CODE_PROCESS_ERROR',
    });
  }
};
